import {
  Flex,
  Grid,
  IconButton,
  IconEditLine,
  IconMasteryPathsLine,
  IconSisSyncedLine,
  IconTrashLine,
  Pill,
  Text,
  ToggleGroup,
  Tooltip,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useContext, useEffect, useState } from "react";
import DataGrid from "react-data-grid";
import { MasteryLevelService } from "../../../services/MasteryLevelService";
import { MasteryLevelContext, UserDetailContext } from "../../../utils/Context";
import { TruncateToolTip } from "../../../utils/TruncateTooltip";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../DialogYesNo";
import DefaultRatingScaleModal from "./DefaultRatingScaleModal";
import MapRatingsToMasteryModal from "./MapRatingsToMasteryModal";

const RatingScale = ({ ratingScale, setUpdateMappings }) => {
  const userDetails = useContext(UserDetailContext);
  const { masteryLevels } = useContext(MasteryLevelContext);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);

  useEffect(() => {
    const cols = [];
    const row = [];
    ratingScale.levels.forEach((level, num) => {
      const masteryLevelId = level.mastery_level_id;
      const key = masteryLevelId + "-" + num;
      const selectedLevel = masteryLevels.find(
        (masteryLevel) => masteryLevel.id === masteryLevelId
      );
      cols.push({
        key,
        name: selectedLevel ? (
          <Text size="small">
            {selectedLevel.level}
            {parseInt(level.points) ===
              parseInt(ratingScale.mastery_points) && (
              <Pill color="success" margin="x-small">
                {t("masteryLevels.mastery")}
              </Pill>
            )}
          </Text>
        ) : (
          <Text fontStyle="italic" size="small">
            {t("masteryLevels.notAssigned")}
          </Text>
        ),
      });
      row[key] = (
        <TruncateToolTip
          renderTip={<div style={{ maxWidth: 250 }}>{level.description}</div>}
        >
          {level.points} - {level.description}
        </TruncateToolTip>
      );
    });
    setColumns(cols);
    setRows(row);
  }, [masteryLevels, ratingScale]);

  return (
    <div>
      <ToggleGroup
        toggleLabel={t("masteryLevels.mappingsToggleLabel")}
        summary={
          <Grid hAlign="space-between" vAlign="middle" startAt="large">
            <Grid.Row>
              <Grid.Col width={6}>
                {ratingScale.title}
                {ratingScale.is_default && (
                  <Pill color="success" margin="x-small" size="small">
                    {t("masteryLevels.institutionalDefault")}
                  </Pill>
                )}
                {ratingScale.is_canvas_default && (
                  <Pill color="info" margin="x-small" size="small">
                    {t("masteryLevels.canvasDefault")}
                  </Pill>
                )}
              </Grid.Col>
              <Grid.Col>
                {t("masteryLevels.totalPoints")}
                {Math.max(...ratingScale.levels.map((o) => o.points))}
              </Grid.Col>
              <Grid.Col width="auto">
                <Flex gap="small">
                  {!ratingScale.is_default &&
                    userDetails.isAC() &&
                    ratingScale.is_mapped && (
                      <Flex.Item>
                        <InstitutionalDefaultButton
                          ratingScale={ratingScale}
                          setUpdateMappings={setUpdateMappings}
                        />
                      </Flex.Item>
                    )}
                  {((ratingScale.is_default && userDetails.isAC()) ||
                    !ratingScale.is_default) && (
                    <Flex.Item>
                      <EditScaleButton
                        ratingScale={ratingScale}
                        setUpdateMappings={setUpdateMappings}
                      />
                    </Flex.Item>
                  )}
                  {!ratingScale.is_default &&
                    ratingScale.is_editable &&
                    ratingScale.is_mapped &&
                    !ratingScale.is_canvas_default && (
                      <Flex.Item>
                        <DeleteMappingButton
                          ratingScale={ratingScale}
                          setUpdateMappings={setUpdateMappings}
                        />
                      </Flex.Item>
                    )}
                  {((ratingScale.is_default && userDetails.isAC()) ||
                    !ratingScale.is_default) &&
                    !ratingScale.is_editable &&
                    ratingScale.is_mapped && (
                      <Flex.Item>
                        <UpdateAssociatedScales
                          ratingScaleId={ratingScale.id}
                        />
                      </Flex.Item>
                    )}
                </Flex>
              </Grid.Col>
            </Grid.Row>
          </Grid>
        }
      >
        <View>
          <DataGrid
            className="rdg-light"
            style={{ height: "auto", maxHeight: "250px" }}
            rowHeight={40}
            headerRowHeight={40}
            columns={columns}
            rows={[rows]}
            defaultColumnOptions={{ width: "minmax(250px, auto)" }}
          />
        </View>
      </ToggleGroup>
    </div>
  );
};

export default RatingScale;

const UpdateAssociatedScales = ({ ratingScaleId }) => {
  const showNotification = useNotification();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleUpdateAssociatedScales = async () => {
    await MasteryLevelService.updateAssociatedScalesByMapId(ratingScaleId)
      .then(() => {
        setConfirmOpen(false);
        showNotification(
          t("notifications.general.updateSuccess", {
            obj: t("ratingScale.AssociatedRatingScales"),
          })
        );
      })
      .catch(() => {
        setConfirmOpen(false);
        showNotification(
          t("notifications.general.updateError", {
            obj: t("ratingScale.associatedRatingScales"),
          }),
          ERROR
        );
      });
  };

  return (
    <>
      <Tooltip
        color="primary-inverse"
        renderTip={t("ratingScale.updateAssociatedOutcomeScales")}
        placement="top"
      >
        <IconButton
          screenReaderLabel={t("ratingScale.updateAssociatedOutcomeScales")}
          renderIcon={IconMasteryPathsLine}
          onClick={() => setConfirmOpen(true)}
        />
      </Tooltip>
      <DialogYesNo
        message={t("ratingScale.updateRatingScaleConfirm")}
        openStatus={confirmOpen}
        onClickNo={() => setConfirmOpen(false)}
        onClickYes={handleUpdateAssociatedScales}
      />
    </>
  );
};

const InstitutionalDefaultButton = ({ ratingScale, setUpdateMappings }) => {
  const [openModal, setOpenModal] = useState(false);

  return (
    <>
      <Tooltip
        color="primary-inverse"
        renderTip={t("masteryLevels.setInstitutionalDefault")}
        placement="top"
      >
        <IconButton
          screenReaderLabel={t("masteryLevels.setInstitutionalDefault")}
          renderIcon={IconSisSyncedLine}
          onClick={() => setOpenModal(true)}
        />
      </Tooltip>
      <DefaultRatingScaleModal
        open={openModal}
        setOpen={setOpenModal}
        ratingScale={ratingScale}
        setUpdateMappings={setUpdateMappings}
      />
    </>
  );
};

const EditScaleButton = ({ ratingScale, setUpdateMappings }) => {
  const [openModal, setOpenModal] = useState(false);

  return (
    <>
      <Tooltip
        color="primary-inverse"
        renderTip={t("masteryLevels.editMappingScreenReaderLabel")}
        placement="top"
      >
        <IconButton
          screenReaderLabel={t("masteryLevels.editMappingScreenReaderLabel")}
          renderIcon={IconEditLine}
          onClick={() => setOpenModal(true)}
        />
      </Tooltip>
      <MapRatingsToMasteryModal
        open={openModal}
        setOpen={setOpenModal}
        handleClose={() => setOpenModal(false)}
        ratingScale={ratingScale}
        updateMappings={setUpdateMappings}
      />
    </>
  );
};

const DeleteMappingButton = ({ ratingScale, setUpdateMappings }) => {
  const showNotification = useNotification();
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const deleteMapping = async () => {
    await MasteryLevelService.deleteRatingToMasteryLevelMap(ratingScale.id)
      .then(() => {
        setUpdateMappings(true);
        setDeleteConfirm(false);
        showNotification(
          t("notifications.general.deleteSuccess", { obj: "Rating Scale" })
        );
      })
      .catch(() => {
        showNotification(
          t("notifications.general.deleteError", { obj: "Rating Scale" }),
          ERROR
        );
      });
  };

  return (
    <>
      <Tooltip
        color="primary-inverse"
        renderTip={t("masteryLevels.deleteScale")}
        placement="top"
      >
        <IconButton
          renderIcon={IconTrashLine}
          screenReaderLabel={t("masteryLevels.deleteScale")}
          onClick={() => setDeleteConfirm(true)}
        />
      </Tooltip>
      <DialogYesNo
        message={t("masteryLevels.deleteMappingMessage")}
        openStatus={deleteConfirm}
        onClickNo={() => setDeleteConfirm(false)}
        onClickYes={deleteMapping}
      />
    </>
  );
};
